import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { Feather, Ionicons, MaterialIcons } from "@expo/vector-icons";
import { DndContext, closestCenter, pointerWithin } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Colors from "../../../../Resources/Color";
import MobileScaledTextarea from "../../../Components/MobileScaledTextarea";
import SearchableDropdown from "../../../Components/SearchableDropdown";
import Tooltip from "../../../Components/Tooltip";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import useChevronRotation from "../../../hooks/useChevronRotation";
import { useDevice } from "../../../_layout";
import {
  formatCalorieValue,
  formatMacroValue,
  UI_MACRO_DISPLAY_ORDER,
} from "../../shared/nutritionFormatters";
import { NutritionPlanMealOptionCard } from "./NutritionPlanMealOptionCard";
import {
  commitMealNameValue,
  getMealOptionTotals,
  getLocalizedPlanMealNamePresetItems,
  resolvePlanMealNamePresetKey,
  shouldShowCustomMealNameOption,
} from "../nutritionPlanLogic";
import {
  MAX_MEAL_NOTES_LENGTH,
  MAX_NUTRITION_NAME_LENGTH,
} from "../../nutritionLimits";
import {
  clearPendingDeleteOptionIfMissing,
  clearPendingDeleteOptionOnSelection,
  resolveMealOptionDeleteRequest,
  shouldDismissPendingDeleteFromPointerTarget,
} from "./nutritionPlanMealOptionDeleteUtils";
import { doesNutritionPlanMealLoseFoodPickerOwnership } from "./nutritionPlanFoodPickerScopeUtils";
import {
  resolvePendingAutoOpenFoodPickerOptionIdForNewOption,
  shouldAutoOpenPendingFoodPicker,
} from "./nutritionPlanMealOptionAutoOpenUtils";
import { beginNutritionPlanOptionPillEdit } from "./nutritionPlanOptionPillEditing";

function restrictHorizontalDrag({ transform }) {
  return {
    ...transform,
    y: 0,
  };
}

function horizontalPointerCollisionDetection(args) {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  return closestCenter(args);
}

function MealStatsStrip({ entries, variant }) {
  return (
    <View
      style={[
        styles.statsStrip,
        variant === "expanded" ? styles.statsStripExpanded : styles.statsStripCollapsed,
      ]}
    >
      {entries.map((entry) => (
        <View key={entry.key} style={styles.statCell}>
          <View style={styles.statLabelRow}>
            <View style={styles.statLabelAdornment}>
              <View
                style={[
                  styles.statLabelDot,
                  { backgroundColor: entry.color ?? Colors.grayColor },
                ]}
              />
            </View>
            <Text style={styles.statLabel}>{entry.label}</Text>
            <View style={styles.statLabelAdornment} />
          </View>
          <View style={styles.statValueRow}>
            <Text style={styles.statValue}>{entry.value}</Text>
            <Text style={styles.statUnit}>{entry.unit}</Text>
          </View>
        </View>
      ))}
    </View>
  );
}

function SortableOptionPill({
  option,
  isSelected,
  isMealHovered,
  isFoodDragActive,
  isPendingDeleteConfirmation,
  onRegisterNode,
  onSelect,
  onStartEditing,
  onUpdateOptionName,
  onRemoveOption,
  canRemoveOption,
  t,
}) {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditHovered, setIsEditHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [optionNameDraft, setOptionNameDraft] = useState(
    typeof option?.name === "string" ? option.name : "",
  );
  const inputRef = useRef(null);
  const optionNameOriginalRef = useRef(
    typeof option?.name === "string" ? option.name : "",
  );
  const optionNameDraftRef = useRef(
    typeof option?.name === "string" ? option.name : "",
  );
  const skipNextOptionNameCommitRef = useRef(false);
  const skipNextOptionNameBlurCommitRef = useRef(false);
  const pendingOptionNameBlurTimerRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: option.id,
      disabled: isFoodDragActive,
    });
  const optionName =
    option.name?.trim() || t("nutritionPlanOptionName-string", "Option name");
  const shouldShowPillDrag = !isEditing && (isMobile || isHovered || isDragging);
  const shouldShowPillEdit = !isEditing && (isMobile || isMealHovered);
  const pillDragOpacity = !isMobile && !isHovered && !isDragging ? 0 : 1;
  const pillEditOpacity = !isMobile && !isMealHovered ? 0 : 1;
  const deleteButtonLabel = t("confirm-string", "Confirm");

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const nextName = typeof option?.name === "string" ? option.name : "";
    optionNameOriginalRef.current = nextName;
    optionNameDraftRef.current = nextName;
    setOptionNameDraft(nextName);
  }, [isEditing, option?.name]);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      const input = inputRef.current;
      input?.focus?.();

      const textLength = input?.value?.length;
      if (
        typeof textLength === "number" &&
        typeof input?.setSelectionRange === "function"
      ) {
        input.setSelectionRange(0, textLength);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isEditing]);

  useEffect(
    () => () => {
      if (pendingOptionNameBlurTimerRef.current) {
        clearTimeout(pendingOptionNameBlurTimerRef.current);
      }
    },
    [],
  );

  const commitOptionName = useCallback(() => {
    skipNextOptionNameBlurCommitRef.current = true;
    if (skipNextOptionNameCommitRef.current) {
      skipNextOptionNameCommitRef.current = false;
      setIsEditing(false);
      return;
    }

    if (pendingOptionNameBlurTimerRef.current) {
      clearTimeout(pendingOptionNameBlurTimerRef.current);
      pendingOptionNameBlurTimerRef.current = null;
    }

    const next =
      typeof optionNameDraftRef.current === "string"
        ? optionNameDraftRef.current.trim()
        : "";
    const current = typeof option?.name === "string" ? option.name : "";

    if (next !== current) {
      onUpdateOptionName(option.id, next);
    }

    setIsEditing(false);
  }, [onUpdateOptionName, option.id, option?.name]);

  const cancelOptionNameEdit = useCallback(() => {
    skipNextOptionNameCommitRef.current = true;
    skipNextOptionNameBlurCommitRef.current = true;
    if (pendingOptionNameBlurTimerRef.current) {
      clearTimeout(pendingOptionNameBlurTimerRef.current);
      pendingOptionNameBlurTimerRef.current = null;
    }

    const original =
      typeof optionNameOriginalRef.current === "string"
        ? optionNameOriginalRef.current
        : "";
    optionNameDraftRef.current = original;
    setOptionNameDraft(original);
    setIsEditing(false);
    inputRef.current?.blur?.();
  }, []);

  const handleOptionNameChange = useCallback((value) => {
    optionNameDraftRef.current = value;
    setOptionNameDraft(value);
  }, []);

  const handleOptionNameBlur = useCallback(() => {
    if (skipNextOptionNameBlurCommitRef.current) {
      skipNextOptionNameBlurCommitRef.current = false;
      return;
    }

    if (pendingOptionNameBlurTimerRef.current) {
      clearTimeout(pendingOptionNameBlurTimerRef.current);
    }

    pendingOptionNameBlurTimerRef.current = setTimeout(() => {
      pendingOptionNameBlurTimerRef.current = null;
      commitOptionName();
    }, 120);
  }, [commitOptionName]);

  const handleStartEditing = useCallback(() => {
    const editState = beginNutritionPlanOptionPillEdit({
      option,
      onSelect,
      onStartEditing,
      preserveSelection: true,
    });
    optionNameOriginalRef.current = editState.currentName;
    optionNameDraftRef.current = editState.draftValue;
    setOptionNameDraft(editState.draftValue);
    skipNextOptionNameCommitRef.current = editState.shouldSkipNextCommit;
    skipNextOptionNameBlurCommitRef.current =
      editState.shouldSkipNextBlurCommit;
    setIsEditing(true);
  }, [onSelect, onStartEditing, option.id, option?.name]);

  const handleInputKeyDown = useCallback(
    (event) => {
      const key = event?.nativeEvent?.key ?? event?.key;
      if (key === "Enter") {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        commitOptionName();
        return;
      }

      if (key === "Escape") {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        cancelOptionNameEdit();
      }
    },
    [cancelOptionNameEdit, commitOptionName],
  );

  return (
    <View
      ref={(node) => {
        setNodeRef(node);
        onRegisterNode?.(option.id, node);
      }}
      style={[
        styles.pillWrap,
        isDragging ? styles.pillWrapDragging : null,
        {
          transform: CSS.Transform.toString(
            transform
              ? {
                  ...transform,
                  scaleX: 1,
                  scaleY: 1,
                }
              : null,
          ),
          transition,
          opacity: isDragging ? 0.88 : 1,
        },
      ]}
      onMouseEnter={Platform.OS === "web" ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === "web" ? () => setIsHovered(false) : undefined}
    >
      <View
        style={[
          styles.pillInner,
          isSelected ? styles.pillInnerSelected : null,
          isHovered && !isSelected && !isEditing ? styles.pillInnerHovered : null,
        ]}
      >
        <View
          style={styles.pillLeadingSlot}
          pointerEvents={shouldShowPillDrag ? "auto" : "none"}
        >
          <View
            {...attributes}
            {...listeners}
            style={styles.pillDragButton}
            accessibilityLabel={t(
              "nutritionPlanReorderOptionA11y-string",
              "Reorder option",
            )}
          >
            <MaterialIcons
              name="drag-indicator"
              size={14}
              color={
                isDragging
                  ? Colors.darkgrayColor
                  : isSelected
                    ? Colors.bubbleColor
                    : Colors.grayColor
              }
              style={[styles.pillActionIcon, { opacity: pillDragOpacity }]}
            />
          </View>
        </View>
        <HoverableTouchableOpacity
          activeOpacity={0.88}
          onPress={() => {
            if (!isEditing) {
              onSelect(option.id);
            }
          }}
          style={[styles.pill, isSelected ? styles.pillSelected : null]}
        >
          {isEditing ? (
            <TextInput
              ref={inputRef}
              value={optionNameDraft}
              onChangeText={handleOptionNameChange}
              maxLength={MAX_NUTRITION_NAME_LENGTH}
              onBlur={handleOptionNameBlur}
              onKeyDown={handleInputKeyDown}
              onKeyPress={handleInputKeyDown}
              onSubmitEditing={commitOptionName}
              blurOnSubmit={true}
              returnKeyType="done"
              placeholder={t("nutritionPlanOptionName-string", "Option name")}
              placeholderTextColor={Colors.grayColor}
              style={[styles.pillNameInput, isSelected ? styles.pillLabelSelected : null]}
              selectTextOnFocus={true}
              accessibilityLabel={t("nutritionPlanOptionName-string", "Option name")}
              aria-label={t("nutritionPlanOptionName-string", "Option name")}
            />
          ) : (
            <Text
              style={[styles.pillLabel, isSelected ? styles.pillLabelSelected : null]}
              numberOfLines={1}
            >
              {optionName}
            </Text>
          )}
        </HoverableTouchableOpacity>
        <View
          style={styles.pillTrailingActions}
          pointerEvents={isEditing || shouldShowPillEdit ? "auto" : "none"}
        >
          {isEditing ? (
            <>
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPressIn={() => {
                  skipNextOptionNameCommitRef.current = true;
                  skipNextOptionNameBlurCommitRef.current = true;
                  if (pendingOptionNameBlurTimerRef.current) {
                    clearTimeout(pendingOptionNameBlurTimerRef.current);
                    pendingOptionNameBlurTimerRef.current = null;
                  }
                }}
                onPress={cancelOptionNameEdit}
                style={styles.pillIconButton}
                hoverStyle={styles.pillActionButtonHover}
                accessibilityLabel={t("cancel-string", "Cancel")}
              >
                <Feather name="x" size={14} color={Colors.grayColor} />
              </HoverableTouchableOpacity>
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPressIn={() => {
                  skipNextOptionNameCommitRef.current = false;
                  skipNextOptionNameBlurCommitRef.current = true;
                  if (pendingOptionNameBlurTimerRef.current) {
                    clearTimeout(pendingOptionNameBlurTimerRef.current);
                    pendingOptionNameBlurTimerRef.current = null;
                  }
                }}
                onPress={commitOptionName}
                style={styles.pillIconButton}
                hoverStyle={styles.pillActionButtonHover}
                accessibilityLabel={t("save-string", "Save")}
              >
                <Ionicons name="checkmark" size={14} color={Colors.bubbleColor} />
              </HoverableTouchableOpacity>
            </>
          ) : (
            <HoverableTouchableOpacity
              activeOpacity={0.85}
              onPress={handleStartEditing}
              style={styles.pillIconButton}
              onMouseEnter={
                Platform.OS === "web" ? () => setIsEditHovered(true) : undefined
              }
              onMouseLeave={
                Platform.OS === "web" ? () => setIsEditHovered(false) : undefined
              }
              accessibilityLabel={t("edit-string", "Edit")}
            >
              <Feather
                name="edit-2"
                size={11}
                color={Colors.grayColor}
                style={[
                  styles.pillActionIcon,
                  { opacity: pillEditOpacity === 0 ? 0 : isEditHovered ? 0.7 : 1 },
                ]}
              />
            </HoverableTouchableOpacity>
          )}
        </View>
        {!isEditing && canRemoveOption && typeof onRemoveOption === "function" ? (
          <View style={styles.pillDeleteWrap}>
            <View
              style={[
                styles.pillDeleteDivider,
                isSelected ? styles.pillDeleteDividerSelected : null,
              ]}
            />
            <HoverableTouchableOpacity
              activeOpacity={0.85}
              onPress={() => onRemoveOption(option.id)}
              style={[
                styles.pillDeleteBtn,
                isPendingDeleteConfirmation ? styles.pillDeleteBtnConfirm : null,
              ]}
              hoverStyle={
                isPendingDeleteConfirmation
                  ? styles.pillDeleteBtnConfirmHover
                  : styles.pillDeleteBtnHover
              }
              onMouseEnter={
                Platform.OS === "web" ? () => setIsDeleteHovered(true) : undefined
              }
              onMouseLeave={
                Platform.OS === "web" ? () => setIsDeleteHovered(false) : undefined
              }
              accessibilityLabel={
                isPendingDeleteConfirmation
                  ? deleteButtonLabel
                  : t("delete-string", "Delete")
              }
            >
              <View style={styles.pillDeleteBtnContent}>
                <Ionicons
                  name="trash-outline"
                  size={14}
                  color={isDeleteHovered ? Colors.darkgrayColor : Colors.grayColor}
                  style={[
                    styles.pillDeleteIcon,
                    isPendingDeleteConfirmation
                      ? styles.pillDeleteIconHidden
                      : styles.pillDeleteIconVisible,
                  ]}
                />
                <Text
                  style={[
                    styles.pillDeleteConfirmLabel,
                    isPendingDeleteConfirmation
                      ? styles.pillDeleteConfirmLabelVisible
                      : styles.pillDeleteConfirmLabelHidden,
                  ]}
                >
                  {deleteButtonLabel}
                </Text>
              </View>
            </HoverableTouchableOpacity>
          </View>
        ) : null}
      </View>
    </View>
  );
}

export function NutritionPlanMealCard({
  coach,
  client = null,
  t,
  language,
  meal,
  mealUiState = null,
  onMealUiStateChange,
  openInlinePickerOwner = null,
  foodPickerOpen: controlledFoodPickerOpen,
  onFoodPickerOpenChange,
  selectedOptionId = "",
  onSelectOption,
  selectedOptionUiState = null,
  onSelectedOptionUiStateChange,
  onInlinePickerOpenChange,
  forceCollapsedForMealDrag = false,
  compactLayout,
  sensors,
  dragHandleProps = null,
  updateMealName,
  updateMealNotes,
  requestRemoveMeal,
  duplicateMeal,
  addOption,
  reorderOptions,
  updateOptionName,
  removeOption,
  hasCopiedOptionFoods = false,
  onCopyOptionFoods,
  onPasteOptionFoods,
  onAddFood,
  isFoodDragActive = false,
  activeFoodDragItemId = "",
  foodDragTarget = null,
  foodDragPlaceholderHeight = 0,
  registerFoodOptionBodyNode,
  registerFoodRowNode,
  getFoodDragHandleProps,
  updateItemQuantity,
  updateItemName,
  updateItemUnit,
  removeItem,
  updateEquivalenceQuantity,
  updateEquivalenceName,
  updateEquivalenceUnit,
  toggleEquivalenceQuantityMode,
  removeEquivalence,
}) {
  const { isMobile } = useDevice();
  const [pendingDeleteOptionId, setPendingDeleteOptionId] = useState(null);
  const [pendingAutoOpenFoodPickerOptionId, setPendingAutoOpenFoodPickerOptionId] =
    useState(null);
  const [internalFoodPickerOpen, setInternalFoodPickerOpen] = useState(false);
  const [internalMealBodyExpanded, setInternalMealBodyExpanded] = useState(true);
  const [isMealHovered, setIsMealHovered] = useState(false);
  const [isMealHeaderHovered, setIsMealHeaderHovered] = useState(false);
  const [isMealDragHandleHovered, setIsMealDragHandleHovered] = useState(false);
  const [internalMealNameMenuOpen, setInternalMealNameMenuOpen] = useState(false);
  const [isNotesFocused, setIsNotesFocused] = useState(false);
  const notesTextareaRef = useRef(null);
  const shouldFocusNotesRef = useRef(false);
  const shouldAutoOpenNewOptionFoodPickerRef = useRef(false);
  const optionNodeMapRef = useRef(new Map());
  const hasMealNotes = String(meal.notes ?? "").trim().length > 0;
  const [internalAreNotesVisible, setInternalAreNotesVisible] = useState(hasMealNotes);
  const mealBodyExpanded = mealUiState?.expanded ?? internalMealBodyExpanded;
  const isMealNameMenuOpen =
    mealUiState?.isMealNameMenuOpen ?? internalMealNameMenuOpen;
  const areNotesVisible = mealUiState?.areNotesVisible ?? internalAreNotesVisible;
  const shouldRenderNotes = hasMealNotes || areNotesVisible || isNotesFocused;
  const shouldRenderMealBody = mealBodyExpanded && !forceCollapsedForMealDrag;
  const mealChevronRotation = useChevronRotation(shouldRenderMealBody);
  const isFoodPickerControlled =
    typeof controlledFoodPickerOpen === "boolean" &&
    typeof onFoodPickerOpenChange === "function";
  const isFoodPickerOpen = isFoodPickerControlled
    ? controlledFoodPickerOpen
    : internalFoodPickerOpen;
  const doesMealLoseFoodPickerOwnership =
    doesNutritionPlanMealLoseFoodPickerOwnership({
      openInlinePickerOwner,
      mealId: meal.id,
    });
  const setFoodPickerOpen = isFoodPickerControlled
    ? onFoodPickerOpenChange
    : setInternalFoodPickerOpen;
  const setMealBodyExpanded = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function" ? value(mealBodyExpanded) : value;
      const normalizedNextValue = nextValue !== false;
      if (typeof onMealUiStateChange === "function") {
        onMealUiStateChange((current) =>
          current.expanded === normalizedNextValue
            ? current
            : {
                ...current,
                expanded: normalizedNextValue,
              },
        );
        return;
      }

      setInternalMealBodyExpanded(normalizedNextValue);
    },
    [mealBodyExpanded, onMealUiStateChange],
  );
  const setIsMealNameMenuOpen = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function" ? value(isMealNameMenuOpen) : value;
      const normalizedNextValue = nextValue === true;
      if (typeof onMealUiStateChange === "function") {
        onMealUiStateChange((current) =>
          current.isMealNameMenuOpen === normalizedNextValue
            ? current
            : {
                ...current,
                isMealNameMenuOpen: normalizedNextValue,
              },
        );
        return;
      }

      setInternalMealNameMenuOpen(normalizedNextValue);
    },
    [isMealNameMenuOpen, onMealUiStateChange],
  );
  const setAreNotesVisible = useCallback(
    (value) => {
      const nextValue =
        typeof value === "function" ? value(areNotesVisible) : value;
      const normalizedNextValue = nextValue === true;
      if (typeof onMealUiStateChange === "function") {
        onMealUiStateChange((current) =>
          current.areNotesVisible === normalizedNextValue
            ? current
            : {
                ...current,
                areNotesVisible: normalizedNextValue,
              },
        );
        return;
      }

      setInternalAreNotesVisible(normalizedNextValue);
    },
    [areNotesVisible, onMealUiStateChange],
  );

  useEffect(() => {
    if (!shouldRenderMealBody) {
      setFoodPickerOpen(false);
      setPendingDeleteOptionId(null);
      setPendingAutoOpenFoodPickerOptionId(null);
      shouldAutoOpenNewOptionFoodPickerRef.current = false;
    }
  }, [shouldRenderMealBody]);

  useEffect(() => {
    if (!doesMealLoseFoodPickerOwnership) {
      return;
    }

    setPendingAutoOpenFoodPickerOptionId(null);
    shouldAutoOpenNewOptionFoodPickerRef.current = false;
  }, [doesMealLoseFoodPickerOwnership]);

  useEffect(() => {
    if (hasMealNotes) {
      setAreNotesVisible(true);
      return;
    }

    if (!isNotesFocused) {
      setAreNotesVisible(false);
    }
  }, [hasMealNotes, isNotesFocused]);

  useEffect(() => {
    if (!shouldFocusNotesRef.current || !shouldRenderMealBody || !areNotesVisible) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      const textarea = notesTextareaRef.current;
      textarea?.focus?.();

      const textLength = textarea?.value?.length;
      if (
        typeof textLength === "number" &&
        typeof textarea?.setSelectionRange === "function"
      ) {
        textarea.setSelectionRange(textLength, textLength);
      }

      shouldFocusNotesRef.current = false;
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [areNotesVisible, shouldRenderMealBody]);

  useEffect(() => {
    setPendingDeleteOptionId(null);
    if (!selectedOptionId && meal.options[0]?.id) {
      onSelectOption?.(meal.options[0].id);
    }
  }, [meal.id, meal.options[0]?.id, onSelectOption, selectedOptionId]);

  useEffect(() => {
    if (!meal.options.some((o) => o.id === selectedOptionId)) {
      onSelectOption?.(meal.options[0]?.id ?? "");
    }
  }, [meal.options, onSelectOption, selectedOptionId]);

  useEffect(() => {
    if (meal.options.length > 0) {
      return;
    }

    setPendingDeleteOptionId(null);
    addOption(meal.id);
  }, [addOption, meal.id, meal.options.length]);

  useEffect(() => {
    const nextPendingDeleteOptionId = clearPendingDeleteOptionIfMissing(
      pendingDeleteOptionId,
      meal.options,
    );
    if (nextPendingDeleteOptionId !== pendingDeleteOptionId) {
      setPendingDeleteOptionId(nextPendingDeleteOptionId);
    }
  }, [meal.options, pendingDeleteOptionId]);

  useEffect(() => {
    setPendingAutoOpenFoodPickerOptionId((currentPendingOptionId) => {
      const normalizedPendingOptionId = String(currentPendingOptionId ?? "").trim();
      if (!normalizedPendingOptionId) {
        return null;
      }

      const targetOption = meal.options.find(
        (option) => option?.id === normalizedPendingOptionId,
      );
      if (!targetOption) {
        return null;
      }

      return Array.isArray(targetOption.items) && targetOption.items.length > 0
        ? null
        : currentPendingOptionId;
    });
  }, [meal.options]);

  useEffect(() => {
    if (
      Platform.OS !== "web" ||
      !pendingDeleteOptionId ||
      typeof document?.addEventListener !== "function"
    ) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const pendingNode = optionNodeMapRef.current.get(pendingDeleteOptionId) ?? null;
      if (
        !shouldDismissPendingDeleteFromPointerTarget(
          event?.target ?? null,
          pendingNode,
        )
      ) {
        return;
      }

      setPendingDeleteOptionId(null);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [pendingDeleteOptionId]);

  const prevOptionCountRef = useRef(meal.options.length);
  useEffect(() => {
    if (meal.options.length > prevOptionCountRef.current) {
      const last = meal.options[meal.options.length - 1];
      if (last) {
        onSelectOption?.(last.id);
        setPendingDeleteOptionId(null);
        setPendingAutoOpenFoodPickerOptionId(
          resolvePendingAutoOpenFoodPickerOptionIdForNewOption({
            previousOptionCount: prevOptionCountRef.current,
            options: meal.options,
            shouldAutoOpenNewOption: shouldAutoOpenNewOptionFoodPickerRef.current,
          }),
        );
      }
    }

    shouldAutoOpenNewOptionFoodPickerRef.current = false;
    prevOptionCountRef.current = meal.options.length;
  }, [meal.options, onSelectOption]);

  const selectedOption =
    meal.options.find((o) => o.id === selectedOptionId) ?? meal.options[0];
  const resolvedSelectedOptionId = selectedOption?.id ?? "";
  const selectedOptionIndex = selectedOption
    ? meal.options.findIndex((o) => o.id === selectedOption.id)
    : -1;

  const selectedOptionTotals = selectedOption
    ? getMealOptionTotals(selectedOption)
    : getMealOptionTotals({ items: [] });
  const shouldShowMealNameEditButton =
    isMealHeaderHovered || isMobile || isMealNameMenuOpen;
  const mealNameOptions = getLocalizedPlanMealNamePresetItems(language, t);

  useEffect(() => {
    if (
      !shouldAutoOpenPendingFoodPicker({
        pendingOptionId: pendingAutoOpenFoodPickerOptionId,
        selectedOptionId: resolvedSelectedOptionId,
        option: selectedOption,
        shouldRenderMealBody,
      })
    ) {
      return;
    }

    setFoodPickerOpen(true);
    setPendingAutoOpenFoodPickerOptionId(null);
  }, [
    pendingAutoOpenFoodPickerOptionId,
    resolvedSelectedOptionId,
    selectedOption,
    shouldRenderMealBody,
  ]);

  const handleShowNotes = useCallback(() => {
    shouldFocusNotesRef.current = true;
    setMealBodyExpanded(true);
    setAreNotesVisible(true);
  }, [setAreNotesVisible, setMealBodyExpanded]);

  const handleNotesBlur = useCallback(
    (event) => {
      setIsNotesFocused(false);
      const rawValue =
        event?.currentTarget?.value ?? event?.target?.value ?? meal.notes ?? "";

      if (!String(rawValue).trim()) {
        setAreNotesVisible(false);
      }
    },
    [meal.notes],
  );
  const handleSelectMealName = useCallback(
    (nextName) => {
      const resolvedName =
        typeof nextName === "string" ? nextName : String(nextName?.label ?? "");
      updateMealName(meal.id, commitMealNameValue(resolvedName, meal.name));
    },
    [meal.id, meal.name, updateMealName],
  );

  const handleOptionDragEnd = useCallback(
    (event) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = meal.options.findIndex((option) => option.id === active.id);
      const newIndex = meal.options.findIndex((option) => option.id === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      reorderOptions(meal.id, arrayMove(meal.options, oldIndex, newIndex));
    },
    [meal.id, meal.options, reorderOptions],
  );

  const handleOptionDragStart = useCallback(() => {
    setPendingDeleteOptionId(null);
  }, []);

  const handleSelectOption = useCallback((optionId) => {
    onSelectOption?.(optionId);
    setPendingDeleteOptionId((currentPendingDeleteOptionId) =>
      clearPendingDeleteOptionOnSelection(currentPendingDeleteOptionId, optionId),
    );
    setPendingAutoOpenFoodPickerOptionId((currentPendingOptionId) => {
      const normalizedPendingOptionId = String(currentPendingOptionId ?? "").trim();
      const normalizedOptionId = String(optionId ?? "").trim();
      if (!normalizedPendingOptionId || normalizedPendingOptionId === normalizedOptionId) {
        return currentPendingOptionId;
      }

      return null;
    });
  }, [onSelectOption]);

  const handleAddOption = useCallback(() => {
    shouldAutoOpenNewOptionFoodPickerRef.current = true;
    const didAddOption = addOption(meal.id);
    if (didAddOption === false) {
      shouldAutoOpenNewOptionFoodPickerRef.current = false;
    }
  }, [addOption, meal.id]);

  const handleStartOptionEditing = useCallback(() => {
    setPendingDeleteOptionId(null);
  }, []);

  const handleRegisterOptionNode = useCallback((optionId, node) => {
    const normalizedOptionId = String(optionId ?? "").trim();
    if (!normalizedOptionId) {
      return;
    }

    if (node) {
      optionNodeMapRef.current.set(normalizedOptionId, node);
      return;
    }

    optionNodeMapRef.current.delete(normalizedOptionId);
  }, []);

  const handleRequestRemoveOption = useCallback(
    (optionId) => {
      const option = meal.options.find((candidateOption) => candidateOption.id === optionId);
      const { action, nextPendingDeleteOptionId } = resolveMealOptionDeleteRequest(
        option,
        pendingDeleteOptionId,
      );

      if (action === "noop") {
        return;
      }

      if (action === "confirm") {
        onSelectOption?.(optionId);
        setPendingDeleteOptionId(nextPendingDeleteOptionId);
        return;
      }

      setPendingDeleteOptionId(null);
      removeOption(meal.id, optionId);
    },
    [meal.id, meal.options, onSelectOption, pendingDeleteOptionId, removeOption],
  );

  const macroStatEntries = {
    protein: {
      key: "protein",
      label: t("protein-string"),
      color: Colors.proteinColor,
      value: formatMacroValue(selectedOptionTotals.protein),
      unit: "g",
    },
    carbs: {
      key: "carbs",
      label: t("carbs-string"),
      color: Colors.carbsColor,
      value: formatMacroValue(selectedOptionTotals.carbs),
      unit: "g",
    },
    fats: {
      key: "fats",
      label: t("fats-string"),
      color: Colors.fatsColor,
      value: formatMacroValue(selectedOptionTotals.fats),
      unit: "g",
    },
  };
  const statEntries = [
    {
      key: "energy",
      label: t("energy-string"),
      color: Colors.energyColor,
      value: formatCalorieValue(selectedOptionTotals.calories),
      unit: "kcal",
    },
    ...UI_MACRO_DISPLAY_ORDER.map((key) => macroStatEntries[key]),
    {
      key: "fiber",
      label: t("fiber-string"),
      color: Colors.fiberColor,
      value: formatMacroValue(selectedOptionTotals.fiber),
      unit: "g",
    },
  ];

  return (
    <View
      style={styles.card}
      onMouseEnter={Platform.OS === "web" ? () => setIsMealHovered(true) : undefined}
      onMouseLeave={Platform.OS === "web" ? () => setIsMealHovered(false) : undefined}
    >
      <View
        style={[
          styles.header,
          shouldRenderMealBody
            ? styles.headerWhenBodyExpanded
            : styles.headerWhenCollapsed,
        ]}
      >
        <View
          style={styles.headerMain}
          onMouseEnter={
            Platform.OS === "web" ? () => setIsMealHeaderHovered(true) : undefined
          }
          onMouseLeave={
            Platform.OS === "web" ? () => setIsMealHeaderHovered(false) : undefined
          }
        >
          <View style={styles.dragHandleWrap}>
            {dragHandleProps ? (
              <View
                {...dragHandleProps.attributes}
                {...dragHandleProps.listeners}
                style={styles.dragHandleTouch}
                onMouseEnter={
                  Platform.OS === "web"
                    ? () => setIsMealDragHandleHovered(true)
                    : undefined
                }
                onMouseLeave={
                  Platform.OS === "web"
                    ? () => setIsMealDragHandleHovered(false)
                    : undefined
                }
              >
                <MaterialIcons
                  name="drag-indicator"
                  size={16}
                  color={
                    dragHandleProps.isDragging || isMealDragHandleHovered
                      ? Colors.darkgrayColor
                      : Colors.grayColor
                  }
                />
              </View>
            ) : null}
          </View>
          <View style={styles.nameWrap}>
            <View style={styles.mealNameRow}>
              <Text
                style={styles.nameText}
                numberOfLines={2}
                accessibilityLabel={t("nutritionPlanMealName-string", "Meal name")}
                aria-label={t("nutritionPlanMealName-string", "Meal name")}
              >
                {meal.name?.trim() || t("nutritionPlanMealName-string", "Meal name")}
              </Text>
              <View
                style={[
                  styles.mealNameEditWrap,
                  !shouldShowMealNameEditButton ? styles.mealNameEditWrapHidden : null,
                ]}
                pointerEvents={shouldShowMealNameEditButton ? "auto" : "none"}
              >
                <SearchableDropdown
                  items={mealNameOptions}
                  triggerLabel={t("edit-string", "Edit")}
                  selectedKey={resolvePlanMealNamePresetKey(meal.name, {
                    language,
                    t,
                  })}
                  searchPlaceholder={t(
                    "nutritionPlanMealNameSearch-string",
                    "Search or type a meal name",
                  )}
                  emptyLabel={t(
                    "nutritionPlanMealNameEmpty-string",
                    "No matching meal names",
                  )}
                  suggestionsLabel={t(
                    "nutritionPlanMealNameSuggestions-string",
                    "Meal names",
                  )}
                  getItemKey={(item) => item.id}
                  getItemLabel={(item) => item.label}
                  getItemSearchText={(item) =>
                    [item.label, ...(Array.isArray(item.keywords) ? item.keywords : [])]
                      .filter(Boolean)
                      .join(" ")
                  }
                  onSelect={handleSelectMealName}
                  onCreateValue={handleSelectMealName}
                  shouldShowCreate={(query) =>
                    shouldShowCustomMealNameOption(query, {
                      language,
                      t,
                    })
                  }
                  getCreateLabel={(query) => {
                    const nextValue = commitMealNameValue(query);
                    return t("nutritionPlanMealNameUseCustom-string", {
                      value: nextValue,
                      defaultValue: `Use "${nextValue}"`,
                    });
                  }}
                  open={isMealNameMenuOpen}
                  onOpenChange={setIsMealNameMenuOpen}
                  initialQuery=""
                  autoFocusSearch={true}
                  searchInputMaxLength={MAX_NUTRITION_NAME_LENGTH}
                  width={260}
                  maxHeight={220}
                  triggerStyle={styles.mealNameEditTrigger}
                  showTriggerChevron={false}
                  renderTrigger={({ isHovered: isTriggerHovered }) => (
                    <Feather
                      name="edit-2"
                      size={12}
                      color={Colors.grayColor}
                      style={{
                        opacity: isTriggerHovered || isMealNameMenuOpen ? 0.7 : 1,
                      }}
                    />
                  )}
                />
              </View>
            </View>
          </View>
          <View style={styles.headerActions}>
            <Tooltip
              content="nutritionPlanAddAlternativeOption-string"
              placement="top"
              maxWidth={320}
            >
              <View>
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={handleAddOption}
                  style={styles.headerAltOptionIconBtn}
                  hoverStyle={styles.headerAltOptionIconBtnHover}
                  accessibilityLabel={t(
                    "nutritionPlanAddAlternativeOption-string",
                    "Add meal option",
                  )}
                >
                  <MaterialIcons
                    name="format-list-bulleted-add"
                    size={16}
                    color={Colors.darkgrayColor}
                  />
                </HoverableTouchableOpacity>
              </View>
            </Tooltip>
            {!hasMealNotes ? (
              <Tooltip content="nutritionPlanAddNote-string" placement="top">
                <View>
                  <HoverableTouchableOpacity
                    activeOpacity={0.85}
                    onPress={handleShowNotes}
                    style={styles.iconButton}
                    hoverStyle={styles.iconButtonHover}
                    accessibilityLabel={t(
                      "nutritionPlanAddNote-string",
                      "Add note",
                    )}
                  >
                    <Ionicons
                      name="create-outline"
                      size={16}
                      color={Colors.darkgrayColor}
                    />
                  </HoverableTouchableOpacity>
                </View>
              </Tooltip>
            ) : null}
            <Tooltip content="nutritionPlanDuplicateMeal-string" placement="top">
              <View>
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => duplicateMeal?.(meal.id)}
                  style={styles.iconButton}
                  hoverStyle={styles.iconButtonHover}
                  accessibilityLabel={t(
                    "nutritionPlanDuplicateMeal-string",
                    "Duplicate meal",
                  )}
                >
                  <Ionicons
                    name="duplicate-outline"
                    size={16}
                    color={Colors.darkgrayColor}
                  />
                </HoverableTouchableOpacity>
              </View>
            </Tooltip>
            <Tooltip content="nutritionPlanDeleteMeal-string" placement="top">
              <View>
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => requestRemoveMeal(meal.id)}
                  style={styles.iconButton}
                  hoverStyle={styles.iconButtonHover}
                  accessibilityLabel={t(
                    "nutritionPlanDeleteMeal-string",
                    "Delete meal",
                  )}
                >
                  <Ionicons
                    name="trash-outline"
                    size={16}
                    color={Colors.darkgrayColor}
                  />
                </HoverableTouchableOpacity>
              </View>
            </Tooltip>
            <HoverableTouchableOpacity
              activeOpacity={0.85}
              onPress={() => setMealBodyExpanded((open) => !open)}
              style={styles.iconButton}
              hoverStyle={styles.iconButtonHover}
              accessibilityLabel={
                shouldRenderMealBody
                  ? t("nutritionPlanCollapseMealA11y-string", "Collapse meal")
                  : t("nutritionPlanExpandMealA11y-string", "Expand meal")
              }
              aria-expanded={shouldRenderMealBody}
            >
              <Animated.View
                style={{ transform: [{ rotate: mealChevronRotation }] }}
              >
                <Feather
                  name="chevron-down"
                  size={18}
                  color={Colors.darkgrayColor}
                />
              </Animated.View>
            </HoverableTouchableOpacity>
          </View>
        </View>
      </View>

      {!shouldRenderMealBody ? (
        <View style={styles.collapsedStatsBlock}>
          <MealStatsStrip entries={statEntries} variant="collapsed" />
        </View>
      ) : null}

      {shouldRenderMealBody ? (
        <View style={styles.body}>
          {meal.options.length > 1 ? (
            <DndContext
              id={`nutrition-plan-meal-options-${meal.id}`}
              sensors={sensors}
              collisionDetection={horizontalPointerCollisionDetection}
              modifiers={[restrictHorizontalDrag]}
              autoScroll={false}
              onDragStart={handleOptionDragStart}
              onDragEnd={handleOptionDragEnd}
            >
              <View style={styles.pillRowHoverTarget}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.pillScrollContent}
                  keyboardShouldPersistTaps="handled"
                >
                  <View style={styles.pillRow}>
                    <SortableContext
                      items={meal.options.map((o) => o.id)}
                      strategy={horizontalListSortingStrategy}
                    >
                      {meal.options.map((option) => (
                        <SortableOptionPill
                          key={option.id}
                          option={option}
                          isSelected={option.id === resolvedSelectedOptionId}
                          isMealHovered={isMealHovered}
                          isFoodDragActive={isFoodDragActive}
                          isPendingDeleteConfirmation={
                            option.id === pendingDeleteOptionId
                          }
                          onRegisterNode={handleRegisterOptionNode}
                          onSelect={handleSelectOption}
                          onStartEditing={handleStartOptionEditing}
                          onUpdateOptionName={(optionId, value) =>
                            updateOptionName(meal.id, optionId, value)
                          }
                          onRemoveOption={handleRequestRemoveOption}
                          canRemoveOption={meal.options.length > 1}
                          t={t}
                        />
                      ))}
                    </SortableContext>
                    <HoverableTouchableOpacity
                      activeOpacity={0.85}
                      onPress={handleAddOption}
                      style={styles.addPill}
                      hoverStyle={styles.addPillHover}
                      accessibilityLabel={t("addOption-string", "Add option")}
                    >
                      <Ionicons
                        name="add-outline"
                        size={14}
                        color={Colors.grayColor}
                      />
                      <Text style={styles.addPillText}>
                        {t("nutritionPlanAddOptionInline-string", "Option")}
                      </Text>
                    </HoverableTouchableOpacity>
                  </View>
                </ScrollView>
              </View>
            </DndContext>
          ) : null}

          {selectedOption ? (
            <NutritionPlanMealOptionCard
              key={selectedOption.id}
              coach={coach}
              client={client}
              t={t}
              language={language}
              mealId={meal.id}
              option={selectedOption}
              optionUiState={selectedOptionUiState}
              onOptionUiStateChange={onSelectedOptionUiStateChange}
              openInlinePickerOwner={openInlinePickerOwner}
              isDefaultOption={selectedOptionIndex === 0}
              compactLayout={compactLayout}
              showHeader={false}
              mealPlanEmbed
              foodPickerOpen={isFoodPickerOpen}
              onFoodPickerOpenChange={setFoodPickerOpen}
              onInlinePickerOpenChange={onInlinePickerOpenChange}
              isFoodDragActive={isFoodDragActive}
              activeFoodDragItemId={activeFoodDragItemId}
              foodDragTarget={foodDragTarget}
              foodDragPlaceholderHeight={foodDragPlaceholderHeight}
              registerFoodOptionBodyNode={registerFoodOptionBodyNode}
              registerFoodRowNode={registerFoodRowNode}
              getFoodDragHandleProps={getFoodDragHandleProps}
              updateOptionName={(optionId, value) =>
                updateOptionName(meal.id, optionId, value)
              }
              removeOption={handleRequestRemoveOption}
              hasCopiedOptionFoods={hasCopiedOptionFoods}
              onCopyOptionFoods={(optionId) =>
                onCopyOptionFoods?.(meal.id, optionId)
              }
              onPasteOptionFoods={(optionId) =>
                onPasteOptionFoods?.(meal.id, optionId)
              }
              onAddFood={(payload) => onAddFood(meal.id, payload)}
              updateItemQuantity={(optionId, itemId, value) =>
                updateItemQuantity(meal.id, optionId, itemId, value)
              }
              updateItemName={(optionId, itemId, value) =>
                updateItemName(meal.id, optionId, itemId, value)
              }
              updateItemUnit={(optionId, itemId, nextUnitKey) =>
                updateItemUnit(meal.id, optionId, itemId, nextUnitKey)
              }
              removeItem={(optionId, itemId) =>
                removeItem(meal.id, optionId, itemId)
              }
              updateEquivalenceQuantity={(optionId, itemId, equivalenceId, value) =>
                updateEquivalenceQuantity(
                  meal.id,
                  optionId,
                  itemId,
                  equivalenceId,
                  value,
                )
              }
              updateEquivalenceName={(optionId, itemId, equivalenceId, value) =>
                updateEquivalenceName(
                  meal.id,
                  optionId,
                  itemId,
                  equivalenceId,
                  value,
                )
              }
              updateEquivalenceUnit={(
                optionId,
                itemId,
                equivalenceId,
                nextUnitKey,
              ) =>
                updateEquivalenceUnit(
                  meal.id,
                  optionId,
                  itemId,
                  equivalenceId,
                  nextUnitKey,
                )
              }
              toggleEquivalenceQuantityMode={(optionId, itemId, equivalenceId) =>
                toggleEquivalenceQuantityMode(
                  meal.id,
                  optionId,
                  itemId,
                  equivalenceId,
                )
              }
              removeEquivalence={(optionId, itemId, equivalenceId) =>
                removeEquivalence(meal.id, optionId, itemId, equivalenceId)
              }
            />
          ) : null}

          {shouldRenderNotes ? (
            <View style={styles.notesMinimal}>
              <MobileScaledTextarea
                ref={notesTextareaRef}
                className="exercise-notes-textarea"
                value={meal.notes ?? ""}
                onChange={(e) => {
                  const raw = e?.currentTarget?.value ?? e?.target?.value ?? "";
                  updateMealNotes(meal.id, raw);
                }}
                onFocus={() => setIsNotesFocused(true)}
                onBlur={handleNotesBlur}
                placeholder={t("notesDots-string", "Notes...")}
                fontSize={isMobile ? 13 : 12}
                minRows={1}
                maxLength={MAX_MEAL_NOTES_LENGTH}
                autoCorrect="off"
                spellCheck="false"
                style={{
                  width: "100%",
                  resize: "none",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  border: "none",
                  outline: "none",
                  fontWeight: "500",
                  color: Colors.darkgrayColor,
                  backgroundColor: "transparent",
                  padding: 0,
                  margin: 0,
                  textAlign: "left",
                  lineHeight: "normal",
                  maxHeight: 160,
                }}
                accessibilityLabel={t("notes-string", "Notes")}
              />
            </View>
          ) : null}

          <MealStatsStrip entries={statEntries} variant="expanded" />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.whiteColor,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 12,
    backgroundColor: Colors.whiteColor,
  },
  headerWhenBodyExpanded: {
    borderBottomWidth: 0,
  },
  headerWhenCollapsed: {
    borderBottomWidth: 0,
  },
  collapsedStatsBlock: {
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 14,
    backgroundColor: Colors.whiteColor,
  },
  statsStrip: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  statsStripCollapsed: {
    marginTop: 0,
    paddingTop: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: Colors.lightGrayColor,
  },
  statsStripExpanded: {
    marginTop: 4,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: Colors.lightGrayColor,
  },
  statCell: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: 62,
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  statLabel: {
    fontSize: 9,
    fontWeight: "600",
    color: Colors.grayColor,
    letterSpacing: 0.35,
    textTransform: "uppercase",
    textAlign: "center",
  },
  statLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    marginBottom: 3,
  },
  statLabelAdornment: {
    width: 10,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statLabelDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    flexShrink: 0,
  },
  statValueRow: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "center",
    gap: 4,
  },
  statValue: {
    fontSize: 13,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  statUnit: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  headerAltOptionIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  headerAltOptionIconBtnHover: {
    backgroundColor: Colors.hoveredGray,
  },
  notesMinimal: {
    width: "100%",
    marginTop: 2,
  },
  pillRowHoverTarget: {
    marginBottom: 2,
    alignSelf: "stretch",
  },
  headerMain: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  dragHandleWrap: {
    width: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  dragHandleTouch: {
    padding: 4,
    cursor: "grab",
    touchAction: "none",
  },
  nameWrap: {
    flex: 1,
    minWidth: 220,
  },
  mealNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
  },
  nameText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.blackColor,
    flexShrink: 1,
    minWidth: 0,
  },
  mealNameEditWrap: {
    minWidth: 20,
    alignItems: "flex-start",
    justifyContent: "center",
  },
  mealNameEditWrapHidden: {
    opacity: 0,
  },
  mealNameEditTrigger: {
    width: "auto",
    minHeight: 0,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 0,
    backgroundColor: "transparent",
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
  },
  iconButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  iconButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  body: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 14,
    gap: 10,
    backgroundColor: Colors.whiteColor,
  },
  pillScrollContent: {
    flexGrow: 0,
    paddingBottom: 4,
    paddingRight: 4,
  },
  pillRow: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 6,
    paddingRight: 2,
  },
  pillWrap: {
    flexShrink: 0,
    alignSelf: "flex-start",
    position: "relative",
    zIndex: 1,
  },
  pillWrapDragging: {
    zIndex: 20,
    elevation: 20,
  },
  pillInner: {
    flexDirection: "row",
    alignItems: "stretch",
    alignSelf: "flex-start",
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    overflow: "hidden",
  },
  pillInnerHovered: {
    backgroundColor: Colors.hoveredGray,
  },
  pillInnerSelected: {
    borderColor: Colors.bubbleColor,
    borderWidth: 1,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  pillLeadingSlot: {
    width: 28,
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: 4,
  },
  pill: {
    alignItems: "flex-start",
    justifyContent: "center",
    paddingVertical: 7,
    paddingHorizontal: 8,
    minWidth: 0,
    maxWidth: 220,
    flexShrink: 1,
    flexGrow: 0,
  },
  pillNameInput: {
    width: "auto",
    minWidth: 56,
    maxWidth: 160,
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
    outlineStyle: "none",
    padding: 0,
    margin: 0,
  },
  pillSelected: {
    backgroundColor: "transparent",
  },
  pillLabel: {
    width: "auto",
    minWidth: 0,
    fontSize: 12,
    fontWeight: "600",
    color: Colors.blackColor,
    flexShrink: 1,
  },
  pillLabelSelected: {
    color: Colors.bubbleColor,
  },
  pillTrailingActions: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 4,
    gap: 2,
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  pillDragButton: {
    width: 24,
    height: 24,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    touchAction: "none",
  },
  pillIconButton: {
    width: 24,
    height: 24,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  pillActionIcon: {
    transition: "opacity 160ms ease-in-out",
  },
  pillActionButtonHover: {
    opacity: 0.68,
  },
  pillDeleteWrap: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 2,
    paddingRight: 2,
  },
  pillDeleteDivider: {
    width: StyleSheet.hairlineWidth,
    height: 16,
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    marginRight: 2,
  },
  pillDeleteDividerSelected: {
    backgroundColor: "rgba(63, 123, 141, 0.18)",
  },
  pillDeleteBtn: {
    width: 30,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    overflow: "hidden",
    transition: "width 160ms ease, background-color 160ms ease, transform 160ms ease",
  },
  pillDeleteBtnHover: {
    opacity: 0.88,
  },
  pillDeleteBtnConfirm: {
    width: 60,
    borderRadius: 999,
    backgroundColor: "rgba(235, 87, 87, 0.1)",
    transform: [{ scale: 1.02 }],
  },
  pillDeleteBtnConfirmHover: {
    backgroundColor: "rgba(235, 87, 87, 0.14)",
  },
  pillDeleteBtnContent: {
    width: "100%",
    minHeight: 24,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  pillDeleteIcon: {
    position: "absolute",
    transition: "opacity 160ms ease, transform 160ms ease",
  },
  pillDeleteIconVisible: {
    opacity: 1,
    transform: [{ translateX: 0 }, { scale: 1 }],
  },
  pillDeleteIconHidden: {
    opacity: 0,
    transform: [{ translateX: -6 }, { scale: 0.88 }],
  },
  pillDeleteConfirmLabel: {
    position: "absolute",
    fontSize: 11,
    fontWeight: "700",
    color: Colors.redColor,
    textAlign: "center",
    transition: "opacity 160ms ease, transform 160ms ease",
  },
  pillDeleteConfirmLabelVisible: {
    opacity: 1,
    transform: [{ translateX: 0 }, { scale: 1 }],
  },
  pillDeleteConfirmLabelHidden: {
    opacity: 0,
    transform: [{ translateX: 6 }, { scale: 0.92 }],
  },
  addPill: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    paddingVertical: 7,
    paddingHorizontal: 4,
    alignSelf: "center",
    position: "relative",
    zIndex: 0,
  },
  addPillHover: {
    opacity: 0.72,
  },
  addPillText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
});
